// financeHeroTune — 금융탭 자아 카드 HERO 우측 캐릭터 일러 inset 배치
//
// inset 모델(효신님 2026-06-17): 캐릭터를 상/우/하 여백(px)으로 고정, 왼쪽은 자유.
//   → 높이 = 카드높이 − (top + bottom), 폭 = 일러 비율 자동. 코칭(팁) 줄 수가 바뀌어도 여백 일정.
// 캐릭터별 일러 차이(차지 영역)는 INSET_BY_CHAR로 보정 — 튜너 hero-inset-tuner.html "클립보드 복사" 결과를 1:1로 붙임.
//   미설정 캐릭터 = DEFAULT_INSET(20). 소스: 'sq' = 시그니처 / 'card' = L카드(전설 등급 전용 일러).
import { CHAR_ORDER, type Grade } from '@core/sim/cosmetic/cosmeticCatalog';

export type HeroSource = 'sq' | 'card';
// dy·scale은 선택 축(2026-08-19 신설).
//   inset(top/right/bottom)은 **여백**이라 top을 키우면 위치가 아니라 **높이**가 줄어든다.
//   그래서 "위아래로 옮기고 싶다"가 이 모델로는 표현되지 않았다(효신님 지적).
//   dy = translateY(px) · scale = 배율. 미지정이면 0 / 1이라 기존 값과 동작이 같다.
export type Inset = { top: number; right: number; bottom: number; dy?: number; scale?: number };

const DEFAULT_INSET: Inset = { top: 20, right: 20, bottom: 20 };

// 캐릭터별 inset(px) — 튜너(hero-inset-tuner.html) "클립보드 복사" 결과를 1:1로 붙인다.
// 2026-08-19 효신님 조정 반영: 시그니처(sq) 11종 전부. top/bottom은 20 유지, **right만** 캐릭터별 보정.
//   → 일러마다 오른쪽 여백이 달라 같은 20을 주면 어떤 캐릭터는 카드 밖으로 밀리고 어떤 캐릭터는 붕 떴다.
// ⚠ L카드(card)는 미조정 — 현 inset 모델은 top/bottom을 동시에 잡아 **높이만 바뀌고 위아래로 못 움직인다**.
//   세로 이동이 필요하면 translateY 축을 따로 넣어야 한다(효신님 2026-08-19 지적).
const INSET_BY_CHAR: Record<HeroSource, Record<string, Inset>> = {
  sq: {
    kkami:       { top: 20, right: -5,  bottom: 20 },
    bbiyong:     { top: 20, right: -1,  bottom: 20 },
    ryan:        { top: 20, right: -9,  bottom: 20 },
    kongi:       { top: 20, right: 5,   bottom: 20 },
    // 록이는 종전 FinanceHeroCard에 translate(58px,6px) scale(1.05)로 **하드코딩**돼 있었다.
    // 데이터로 옮겨 한 곳에서 관리한다(right는 튜너 조정값, x축 58px은 right에 흡수하지 않고 dy/scale만 이관).
    roki:        { top: 20, right: -12, bottom: 20, dy: 6, scale: 1.05 },
    tobi:        { top: 20, right: 4,   bottom: 20 },
    lumi:        { top: 20, right: -12, bottom: 20 },
    mio:         { top: 20, right: -5,  bottom: 20 },
    yeouni:      { top: 20, right: 6,   bottom: 20 },
    hwangdoongi: { top: 20, right: -14, bottom: 20 },
    bugi:        { top: 20, right: -3,  bottom: 20 },
  },
  // L카드(전설 등급 전용 일러) — 2026-08-19 효신님 조정. 시그니처보다 세로로 길어
  // dy(아래로 내림)·scale(확대)이 함께 필요하다. inset만으로는 높이만 변해 위치가 안 잡혔다.
  card: {
    kkami:       { top: 20, right: 4,  bottom: 20, dy: 32, scale: 1.29 },
    bbiyong:     { top: 20, right: 9,  bottom: 20, dy: 30, scale: 1.28 },
    ryan:        { top: 20, right: 9,  bottom: 20, dy: 27, scale: 1.23 },
    kongi:       { top: 20, right: 15, bottom: 20, dy: 30, scale: 1.24 },
    roki:        { top: 20, right: 12, bottom: 20, dy: 15, scale: 1.16 },
    tobi:        { top: 20, right: 7,  bottom: 20, dy: 26, scale: 1.23 },
    lumi:        { top: 20, right: 8,  bottom: 20, dy: 26, scale: 1.22 },
    mio:         { top: 20, right: 16, bottom: 20, dy: 28, scale: 1.20 },
    yeouni:      { top: 20, right: 11, bottom: 20, dy: 25, scale: 1.23 },
    hwangdoongi: { top: 20, right: 7,  bottom: 20, dy: 19, scale: 1.15 },
    bugi:        { top: 20, right: 1,  bottom: 20, dy: 41, scale: 1.40 },
  },
};

// 시그니처 소스 오버라이드 — 콩이는 피기-허그(돼지저금통) 버전 채택
const SIG_OVERRIDE: Record<string, string> = {
  kongi: '/redesign-assets/sig-override/kongi.png',
};

/** 캐릭터 폴더명 (NN-slug) — CHAR_ORDER 순서 = 01~11 = redesign-assets/char/ 폴더와 1:1 */
function charDir(slug: string): string {
  const idx = (CHAR_ORDER as readonly string[]).indexOf(slug);
  const nn = String((idx < 0 ? 0 : idx) + 1).padStart(2, '0');
  return `${nn}-${slug}`;
}

/** 장착 카드 등급 → 일러 소스. L(전설)만 전용 L카드 일러, 그 외는 시그니처. */
export function gradeToSource(grade: Grade | null | undefined): HeroSource {
  return grade === 'L' ? 'card' : 'sq';
}

/** 우측 캐릭터 일러 src (public 절대경로) */
export function heroCharSrc(source: HeroSource, slug: string): string {
  if (source === 'card') return `/redesign-assets/lcard/${slug}.png`;
  return SIG_OVERRIDE[slug] || `/redesign-assets/char/${charDir(slug)}/sig.png`;
}

/** 캐릭터 inset 여백(px) — 캐릭터별 보정값 없으면 기본 20 */
export function heroInset(source: HeroSource, slug: string): Inset {
  return INSET_BY_CHAR[source]?.[slug] || DEFAULT_INSET;
}
